// Csomagok importálása
import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
axios.defaults.baseURL = process.env.REACT_APP_API_URL;
// Context inicializálása
const AuthContext = createContext();

const normalizeRole = (role) => {
  const normalizedRole = String(role || '').trim().toLocaleLowerCase('hu-HU');
  if (normalizedRole === 'fodrász') return 'Fodrász';
  if (normalizedRole === 'rendszergazda') return 'Rendszergazda';
  if (normalizedRole === 'vásárló') return 'Vásárló';
  return role;
};

const normalizeUser = (userData) => ({
  ...userData,
  role: normalizeRole(userData?.role)
});

export function AuthProvider({ children }) {
  // Állapotváltozók: user adatok, betöltés, sötét mód
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [darkMode, setDarkModeState] = useState(false);
  const [siteName, setSiteName] = useState('Fodrász');
  const [siteTitle, setSiteTitle] = useState('Fodrász');

  // Komponens betöltésekor: Session és téma ellenőrzése LocalStorage-ből
  useEffect(() => {
    axios.get(`/api/settings`)
      .then((response) => {
        if (response.data.site_name) setSiteName(response.data.site_name);
        if (response.data.site_title) setSiteTitle(response.data.site_title);
      })
      .catch((error) => console.error('Hiba a webhely nevének betöltésekor:', error));

    const savedUser = localStorage.getItem('barber_user_data');
    if (savedUser) {
      try {
        const userData = normalizeUser(JSON.parse(savedUser));
        setUser(userData);
        localStorage.setItem('barber_user_data', JSON.stringify(userData));
        // Téma beállítása a user beállításai alapján
        const isDark = userData.dark_mode === 1;
        setDarkModeState(isDark);
        if (isDark) document.documentElement.classList.add('dark');
        else document.documentElement.classList.remove('dark');
      } catch (e) {
        localStorage.removeItem('barber_user_data'); // Hibás adat törlése
      }
    } else {
      // Ha nincs user, vendég téma betöltése
      const savedTheme = localStorage.getItem('barber_dark_mode');
      const isDark = savedTheme === '1';
      setDarkModeState(isDark);
      if (isDark) document.documentElement.classList.add('dark');
      else document.documentElement.classList.remove('dark');
    }
    setLoading(false); // Betöltés befejezve
  }, []);

  // Bejelentkezés logika (API hívás)
  const login = async (email, password) => {
    try {
      const response = await axios.post(`/api/auth/login`, { email, password });
      if (response.data.success) {
        const userData = normalizeUser(response.data.user);
        setUser(userData);
        localStorage.setItem('barber_user_data', JSON.stringify(userData)); // Mentés LocalStorage-be

        // Sötét mód frissítése belépés után
        const isDark = userData.dark_mode === 1;
        setDarkModeState(isDark);
        if (isDark) document.documentElement.classList.add('dark');
        else document.documentElement.classList.remove('dark');

        return { success: true, user: userData };
      }

      // Ha a szerver visszaadott hibát, azt továbbadjuk
      return { success: false, error: response.data.message || "Hibás adatok!" };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || "Hiba a bejelentkezésnél!" };
    }
  };

  // Regisztráció logika (API hívás)
  const register = async (userData) => {
    try {
      const response = await axios.post(`api/auth/register`, userData);
      return { success: response.data.success, error: response.data.message || null };
    } catch (error) {
      return { success: false, error: error.response?.data?.message || "Hiba a regisztráció során!" };
    }
  };

  // Felhasználói adatok frissítése a memóriában és LocalStorage-ben
  const updateUser = (newData) => {
    const updatedUser = { ...user, ...newData };
    setUser(updatedUser);
    localStorage.setItem('barber_user_data', JSON.stringify(updatedUser));
  };

  // Sötét mód váltása (DOM frissítés (Dokumentum Objektum Modell) + Adatbázis / LocalStorage mentés)
  const toggleDarkMode = async () => {
    const newMode = !darkMode;
    setDarkModeState(newMode);

    // Azonnali DOM stílusváltás
    if (newMode) document.documentElement.classList.add('dark');
    else document.documentElement.classList.remove('dark');

    // adatbázis mentése, ha van bejelentkezett user
    if (user) {
      try {
        await axios.put(`/api/user/theme`, {
          userId: user.id,
          darkMode: newMode ? 1 : 0
        });
        updateUser({ ...user, dark_mode: newMode ? 1 : 0 });
      } catch (error) {
        console.error('Hiba a téma mentése során:', error);
        setDarkModeState(!newMode); // Visszavonás hiba esetén
        if (!newMode) document.documentElement.classList.add('dark');
        else document.documentElement.classList.remove('dark');
      }
    } else {
      // Csak LocalStorage, ha vendég
      localStorage.setItem('barber_dark_mode', newMode ? '1' : '0');
    }
  };

  // Kijelentkezés: állapot törlése
  const logout = () => {
    setUser(null);
    localStorage.removeItem('barber_user_data');
  };

  // Provider komponens a state-ek megosztásához
  return (
    <AuthContext.Provider value={{ user, login, logout, register, updateUser, loading, darkMode, toggleDarkMode, siteName, setSiteName, siteTitle, setSiteTitle }}>
      {children}
    </AuthContext.Provider>
  );
}

// Hook a Context egyszerűbb használatához
export const useAuth = () => useContext(AuthContext);